import { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";
import { MediaProject } from "../../types/MediaProject";

/**
 * Composed content (carousels + reels) assembled by the MCP from the media
 * library. Read-only from the admin UI side (the MCP/Claude creates them);
 * the UI lists them and can delete.
 *
 *   Community/{id}/MediaProjects/{projectId}
 */

const projectsCollection = (communityId: string) =>
  firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.MEDIA_PROJECT);

const sanitize = (id: string, data: unknown): MediaProject | null => {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  const kind = d.kind === "reel" ? "reel" : "carousel";
  return {
    id,
    kind,
    title: typeof d.title === "string" ? d.title : undefined,
    caption: typeof d.caption === "string" ? d.caption : undefined,
    status: (typeof d.status === "string" ? d.status : "draft") as MediaProject["status"],
    slides: Array.isArray(d.slides) ? (d.slides as MediaProject["slides"]) : undefined,
    outputUrl: typeof d.outputUrl === "string" ? d.outputUrl : undefined,
    thumbnailUrl: typeof d.thumbnailUrl === "string" ? d.thumbnailUrl : undefined,
    durationSec:
      typeof d.durationSec === "number" && Number.isFinite(d.durationSec)
        ? d.durationSec
        : undefined,
    youtubeVideoId:
      typeof d.youtubeVideoId === "string" ? d.youtubeVideoId : undefined,
    youtubeStatus:
      typeof d.youtubeStatus === "string" ? d.youtubeStatus : undefined,
    sourceAssetIds: Array.isArray(d.sourceAssetIds)
      ? (d.sourceAssetIds.filter((s) => typeof s === "string") as string[])
      : undefined,
    spec: d.spec,
    createdAt: typeof d.createdAt === "string" ? d.createdAt : new Date(0).toISOString(),
    updatedAt: typeof d.updatedAt === "string" ? d.updatedAt : new Date(0).toISOString(),
  };
};

export const listenToMediaProjects = (
  communityId: string,
  onChange: (projects: MediaProject[]) => void,
  onError?: (err: Error) => void
): (() => void) => {
  if (!communityId) {
    onChange([]);
    return () => undefined;
  }
  return projectsCollection(communityId)
    .orderBy("updatedAt", "desc")
    .onSnapshot(
      (snap) =>
        onChange(
          snap.docs
            .map((doc) => sanitize(doc.id, doc.data()))
            .filter((p): p is MediaProject => p !== null)
        ),
      (err) => {
        console.error("Error listening to media projects:", err);
        onError?.(err);
      }
    );
};

export const deleteMediaProject = async (
  communityId: string,
  projectId: string
): Promise<void> => {
  if (!communityId || !projectId) return;
  await projectsCollection(communityId).doc(projectId).delete();
};
